import { TaskTypeStatus, type TaskPayReportItemType, type TaskPayReportRowType } from '@econtractor/types';
import type { TFunction } from 'i18next';
import { tDirName } from 'utils/helpers/i18n/tDirName';
import type { ReportPeriodType } from '../../ReportDate/periodUtils';
import type { ReportTasksViewMode } from '../../helper/reportTasksStorage';
import { formatTaskBinding } from './bindingLabel';
import { sumTaskPay } from './taskPayTotals';
import {
    exportDate,
    exportReportWorkbook,
    formatExportPeriod,
    isExportLocalMode,
    moneyColumns,
    roundExportMoney,
    yesNoLabel,
    type ExportCell,
    type ExportColumn,
} from '../../helper/exportReportWorkbook';

const STATUS_ORDER = [
    TaskTypeStatus.NEW,
    TaskTypeStatus.INWORK,
    TaskTypeStatus.COMPLETED,
    TaskTypeStatus.ACCEPTED,
];

export interface ExportTasksReportParams {
    users: TaskPayReportRowType[];
    dates: [number, number] | null;
    period: ReportPeriodType;
    viewMode: ReportTasksViewMode;
    statuses: number[];
    showZeroTasks: boolean;
    rate: number;
    t: TFunction;
}

const taskStatusLabel = (row: TaskPayReportItemType, t: TFunction): string => {
    const status = Number(row.taskStatus);
    const isOpen =
        status !== TaskTypeStatus.COMPLETED &&
        status !== TaskTypeStatus.ACCEPTED &&
        status !== TaskTypeStatus.CANCELLED &&
        status !== TaskTypeStatus.REJECT;
    const overdue = isOpen && Number(row.dateEnd || 0) > 0 && Number(row.dateEnd) < Date.now();
    if (overdue) return t('Tasks:status.overdue');
    return t(`Tasks:status.${status}`, { defaultValue: String(status) });
};

const headerLines = (
    t: TFunction,
    period: ReportPeriodType,
    dates: [number, number] | null,
    viewMode: ReportTasksViewMode,
    statuses: number[],
    showZeroTasks: boolean,
) => {
    const selected = new Set(statuses.map((status) => Number(status)));
    const statusText = STATUS_ORDER
        .filter((status) => selected.has(status))
        .map((status) => t(`Tasks:status.${status}`))
        .join(', ');

    return [
        { label: t('export.period'), value: formatExportPeriod(t(`dateFilter.${period}`), dates) },
        {
            label: t('export.view'),
            value: viewMode === 'detail' ? t('usersView.detailHint') : t('usersView.summaryHint'),
        },
        { label: t('export.statuses'), value: statusText || '—' },
        { label: t('export.zeroTasks'), value: yesNoLabel(showZeroTasks, t) },
    ];
};

export const exportTasksReport = async ({
    users,
    dates,
    period,
    viewMode,
    statuses,
    showZeroTasks,
    rate,
    t,
}: ExportTasksReportParams): Promise<boolean> => {
    if (!users.length) return false;

    const safeRate = Number(rate) || 1;
    const localMode = isExportLocalMode(safeRate);
    const title = t('common:SideBarUl.itemRepostsTasks');
    const incomeColumns = moneyColumns('income', 'incomeLocal', t('tasksFields.income'), safeRate);
    const lines = headerLines(t, period, dates, viewMode, statuses, showZeroTasks);

    if (viewMode === 'summary') {
        const rows = users.map((user) => {
            const totals = sumTaskPay(user.rows || [], safeRate);
            const row: Record<string, ExportCell> = {
                user: user.userName || '',
                count: user.rows?.length || 0,
                income: roundExportMoney(totals.amount),
            };
            if (!localMode) row.incomeLocal = roundExportMoney(totals.amountRate);
            return row;
        });

        await exportReportWorkbook({
            filePrefix: 'report-tasks',
            sheetName: title,
            title,
            headerLines: lines,
            columns: [
                { key: 'user', header: t('usersFields.user'), width: 32 },
                { key: 'count', header: t('tasksFields.count'), width: 18, kind: 'number' },
                ...incomeColumns,
            ],
            rows,
            totalLabel: t('export.total'),
        });
        return true;
    }

    const columns: ExportColumn[] = [
        { key: 'user', header: t('usersFields.user'), width: 28 },
        { key: 'task', header: t('tasksFields.task'), width: 36 },
        { key: 'type', header: t('tasksFields.type'), width: 22 },
        { key: 'status', header: t('tasksFields.status'), width: 18 },
        { key: 'term', header: t('tasksFields.term'), width: 16, kind: 'date' },
        { key: 'bindingKind', header: t('export.bindingKind'), width: 18 },
        { key: 'bindingName', header: t('export.bindingName'), width: 28 },
        ...incomeColumns,
    ];

    const rows: Record<string, ExportCell>[] = [];
    for (const user of users) {
        for (const item of user.rows || []) {
            const totals = sumTaskPay([item], safeRate);
            const binding = formatTaskBinding(item, t);
            const row: Record<string, ExportCell> = {
                user: user.userName || '',
                task: item.taskName || '',
                type: tDirName(t, item.taskTypeI18nKey, item.taskTypeName) || '',
                status: taskStatusLabel(item, t),
                term: exportDate(item.dateEnd),
                bindingKind: binding.kind,
                bindingName: binding.name,
                income: roundExportMoney(totals.amount),
            };
            if (!localMode) row.incomeLocal = roundExportMoney(totals.amountRate);
            rows.push(row);
        }
    }

    if (!rows.length) return false;

    await exportReportWorkbook({
        filePrefix: 'report-tasks',
        sheetName: title,
        title,
        headerLines: lines,
        columns,
        rows,
        totalLabel: t('export.total'),
    });

    return true;
};
